import { EntityEditor } from "./EntityEditor";
import { useFeatureService } from "./services/useFeatureService";
import { useDynamicEntitiesContext, useDynamicEntitiesPermissions } from "../core/DynamicEntitiesProvider";
import React, { useState } from 'react';
import { LayoutContent } from '../../LayoutContent';
import { FeatureList } from './FeatureList';
import { FeatureEditor } from './FeatureEditor';
import { Input } from '../../Input/Input';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';

export const DynamicEntitiesAdmin: React.FC = () => {
    const { onError } = useDynamicEntitiesContext();
    const { canCreate } = useDynamicEntitiesPermissions();
    const service = useFeatureService();
    const [selectedFeatureId, setSelectedFeatureId] = useState<string | null>(null);
    const [selectedPlansAndCriteriaEntityId, setSelectedPlansAndCriteriaEntityId] = useState<string | null>(null);
    const [selectedPlansAndCriteriaFeatureEntityId, setSelectedPlansAndCriteriaFeatureEntityId] = useState<string | null>(null);
    const [selectedPlansAndCriteriaRenderMode, setSelectedPlansAndCriteriaRenderMode] = useState<'tab' | 'inline' | 'modal'>('tab');
    const [searchTerm, setSearchTerm] = useState('');

    const handleCreatePlansAndCriteria = async (feature: any) => {
        try {
            const code = `${feature.code}_planes_criterios`.toUpperCase();

            // Refetch feature to get full feature entities just in case
            const fullFeature = await service.getById(feature.id);
            const existingEntity = fullFeature.featureEntities?.find((fe: any) => fe.dynamicEntity?.code === code || fe.dynamicEntity?.isPlansAndCriteria);

            if (existingEntity) {
                setSelectedFeatureId(feature.id);
                setSelectedPlansAndCriteriaEntityId(existingEntity.dynamicEntityId);
                setSelectedPlansAndCriteriaFeatureEntityId(existingEntity.id);
                setSelectedPlansAndCriteriaRenderMode(existingEntity.renderMode || 'tab');
            } else {
                const newEntity = await service.createEntity({
                    code: code,
                    description: `Planes y Criterios para ${feature.name}`,
                    multiRecord: false,
                    isReadonly: false,
                    isDisabled: false,
                    isPlansAndCriteria: true
                });

                const featureEntity = await service.addFeatureEntity(feature.id, {
                    featureId: feature.id,
                    dynamicEntityId: newEntity.id,
                    renderMode: 'tab',
                    order: 0
                });

                setSelectedFeatureId(feature.id);
                setSelectedPlansAndCriteriaEntityId(newEntity.id);
                setSelectedPlansAndCriteriaFeatureEntityId(featureEntity.id);
                setSelectedPlansAndCriteriaRenderMode('tab'); // New entities default to tab
            }
        } catch (error) {
            console.error('Error creating plans and criteria entity:', error);
            onError?.(error as Error);
            alert('Error al crear o abrir la entidad de planes y criterios. Revisa la consola.');
        }
    };

    if (selectedPlansAndCriteriaEntityId && selectedFeatureId) {
        return (
                    <EntityEditor
                entityId={selectedPlansAndCriteriaEntityId}
                featureId={selectedFeatureId}
                featureEntityId={selectedPlansAndCriteriaFeatureEntityId || undefined}
                initialRenderMode={selectedPlansAndCriteriaRenderMode}
                onBack={() => {
                    setSelectedPlansAndCriteriaEntityId(null);
                    setSelectedPlansAndCriteriaFeatureEntityId(null);
                    setSelectedPlansAndCriteriaRenderMode('tab');
                    setSelectedFeatureId(null);
                }}
            />
        );
    }

    // If editing a specific feature, the toolbar should reflect that context
    if (selectedFeatureId) {
        // We render FeatureEditor which handles its own internal layout logic,
        // or we could wrap it in LayoutContent here if preferred. 
        // For now, let's just render the editor directly and we can refactor FeatureEditor next.
        return (
            <FeatureEditor
                featureId={selectedFeatureId}
                onBack={() => setSelectedFeatureId(null)}
            />
        );
    }

    return (
        <LayoutContent
            title="Opciones del sistema"
            toolbarEndContent={
                <div className="w-full sm:w-64">
                    <Input
                        inputSize="sm"
                        placeholder="Buscar por código o nombre..."
                        value={searchTerm}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
                        startIcon={<MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />}
                    />
                </div>
            }
        >
            <div className="flex flex-col w-full">
                <FeatureList
                    onSelectFeature={setSelectedFeatureId}
                    onCreatePlansAndCriteria={canCreate ? handleCreatePlansAndCriteria : undefined}
                    searchTerm={searchTerm}
                />
            </div>
        </LayoutContent>
    );
};
